import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useShop } from '../../context/ShopContext';
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Divider,
  Breadcrumbs,
  useTheme as useMuiTheme,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import FarmIcon from '@mui/icons-material/Agriculture';
import CalculateIcon from '@mui/icons-material/Calculate';
import StoreIcon from '@mui/icons-material/Store';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalShipping from '@mui/icons-material/LocalShipping';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import NatureIcon from '@mui/icons-material/Nature';
import ForestIcon from '@mui/icons-material/Forest';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ForumIcon from '@mui/icons-material/Forum';
import GlobalIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import InsightsIcon from '@mui/icons-material/Insights';
import DealIcon from '@mui/icons-material/Handshake';
import ThemeToggle from '../common/ThemeToggle';
import LanguageToggle from '../common/LanguageToggle';
import { useTheme } from '../../theme/ThemeContext';
import { useDashboardNotifications } from '../../context/DashboardNotificationContext';
import { useUser } from '../../context/UserContext';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CircleIcon from '@mui/icons-material/Circle';
import SubscriptionIcon from '@mui/icons-material/CreditCard';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import BugIcon from '@mui/icons-material/BugReport';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SensorsIcon from '@mui/icons-material/Sensors';
import PetsIcon from '@mui/icons-material/Pets';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShieldIcon from '@mui/icons-material/Shield';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import FlightIcon from '@mui/icons-material/Flight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AIAgronomistChatbot from '../ai/AIAgronomistChatbot';
import { getWalkthroughContent } from '../../services/navigationService';

const drawerWidth = 264;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navScrollRef = React.useRef<HTMLDivElement | null>(null);
  const mainContentRef = React.useRef<HTMLDivElement | null>(null);
  const { logout } = useAuth();
  const { activeShop } = useShop();
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { hasModule } = useUser();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileMenuAnchorEl);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const [notificationMenuAnchorEl, setNotificationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const notificationMenuOpen = Boolean(notificationMenuAnchorEl);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useDashboardNotifications();
  const [sectionOverrides, setSectionOverrides] = useState<Record<string, boolean>>({});
  const activeSectionsSeededRef = React.useRef(false);

  // Open every collapsible section with status === 'active' on the very first render so users
  // can see new sub-items (Careers & Labor, AI, etc.) without needing to click the header.
  // Runs once inside the `sections` useMemo below; this ref guards against re-seeding when
  // the user toggles sections manually afterwards.

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const walkthrough = React.useMemo(() => getWalkthroughContent(location.pathname), [location.pathname]);

  React.useEffect(() => {
    if (navScrollRef.current) {
      navScrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
      mainContentRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);

  const handleWalkthroughOpen = () => {
    handleProfileMenuClose();
    setWalkthroughStep(0);
    setWalkthroughOpen(true);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchorEl(event.currentTarget);
  };
  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchorEl(null);
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
    handleNotificationMenuClose();
  };

  // Generate breadcrumbs from current location
  const pathnames = location.pathname.split('/').filter((x) => x);
  const breadcrumbNameMap: { [key: string]: string } = {
    dashboard: 'Dashboard',
    'my-farms': 'My Farms',
    'my-shops': 'My Shops',
    calculator: 'Farm Calculator',
    marketplace: 'Marketplace',
    payment: 'Payments',
    logistics: 'Logistics',
    community: 'Community',
    profile: 'Profile',
    settings: 'Settings',
    'smart-farming': 'Smart Farming',
    'carbon-credits': 'Carbon Credits',
    'products': 'Manage Products',
    'orders': 'Seller Orders',
    inbox: 'Inbox',
    careers: 'Careers & Labor',
    'find-work': 'Find Work',
    post: 'Post a Job',
    job: 'Job Details',
  };

  const renderBreadcrumbs = () => {
    if (pathnames.length === 0) return null;

    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          flexGrow: 1,
          color: 'primary.contrastText',
          '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.72)' },
        }}
      >
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          
          // Hide UUIDs/Database IDs from breadcrumbs and use human-readable names
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
          if (isUuid) return null; // Skip IDs in breadcrumbs

          const displayName = breadcrumbNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1);

          return last ? (
            <Typography color="inherit" key={to}>
              {displayName}
            </Typography>
          ) : (
            <Link
              color="inherit"
              to={to}
              key={to}
              style={{
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              {displayName}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  type NavItem = {
    key: string;
    label: string;
    icon: React.ReactNode;
    to: string;
    match?: 'exact' | 'prefix';
  };

  type NavSection = {
    key: string;
    label: string;
    visible: boolean;
    collapsible: boolean;
    status: 'active' | 'setup_required' | 'ready';
    statusNote?: string;
    items: NavItem[];
  };

  const isItemActive = (item: NavItem) => {
    if (item.match === 'prefix') {
      return location.pathname.startsWith(item.to);
    }
    return location.pathname === item.to;
  };

  const navItemSx = (selected: boolean) => ({
    borderRadius: '12px',
    mb: 0.75,
    py: 1,
    transition: 'background-color 160ms ease, border-color 160ms ease',
    borderLeft: '3px solid',
    borderLeftColor: selected ? 'primary.main' : 'transparent',
    bgcolor: selected ? alpha(muiTheme.palette.primary.main, isDarkMode ? 0.22 : 0.12) : 'transparent',
    '&:hover': {
      bgcolor: alpha(muiTheme.palette.primary.main, isDarkMode ? 0.16 : 0.08),
    },
  });

  const sectionHeaderSx = (active: boolean) => ({
    px: 1,
    py: 1,
    mt: 1,
    mb: 0.5,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    bgcolor: active ? alpha(muiTheme.palette.primary.main, isDarkMode ? 0.14 : 0.08) : 'transparent',
    '&:hover': {
      bgcolor: alpha(muiTheme.palette.primary.main, isDarkMode ? 0.14 : 0.06),
    },
  });

  const getSectionStatusProps = (status: NavSection['status']) => {
    switch (status) {
      case 'setup_required':
        return {
          label: 'Setup',
          color: 'warning' as const,
          variant: 'filled' as const,
        };
      case 'active':
        return {
          label: 'Active',
          color: 'success' as const,
          variant: 'filled' as const,
        };
      default:
        return {
          label: 'Ready',
          color: 'default' as const,
          variant: 'outlined' as const,
        };
    }
  };

  const sections: NavSection[] = useMemo(() => {
    const core: NavSection = {
      key: 'core',
      label: 'Core',
      visible: true,
      collapsible: false,
      status: 'ready',
      statusNote: 'Home and account essentials',
      items: [
        { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon sx={{ width: 20, height: 20 }} />, to: '/dashboard' },
        {
          key: 'notifications',
          label: 'Notifications',
          icon: <NotificationsIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/notifications',
        },
        ...(hasModule('marketplace_buyer') || hasModule('marketplace_seller')
          ? ([
              {
                key: 'inbox',
                label: 'Inbox',
                icon: <ForumIcon sx={{ width: 20, height: 20 }} />,
                to: '/dashboard/inbox',
              },
            ] as NavItem[])
          : []),
      ],
    };

    const farm: NavSection = {
      key: 'farm',
      label: 'Farm Management',
      visible: hasModule('farm_management'),
      collapsible: true,
      status: 'active',
      statusNote: 'Daily farm operations',
      items: [
        {
          key: 'my-farms',
          label: 'My Farms',
          icon: <FarmIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/my-farms',
          match: 'prefix',
        },
        {
          key: 'calculator',
          label: 'Farm Calculator',
          icon: <CalculateIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/calculator',
        },
        {
          key: 'farm-tasks',
          label: 'Farm Tasks',
          icon: <ReceiptIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/farm-tasks',
        },
        {
          key: 'workers',
          label: 'Workers',
          icon: <GroupIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/workers',
        },
        {
          key: 'weather',
          label: 'Weather',
          icon: <NatureIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/weather',
        },
        {
          key: 'smart-farming',
          label: 'Smart Farming',
          icon: <SensorsIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/smart-farming',
        },
        {
          key: 'pest-detection',
          label: 'Pest Detection',
          icon: <BugIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/ai/pest-detection',
        },
        {
          key: 'livestock',
          label: 'Livestock Tracking',
          icon: <PetsIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/livestock-tracking',
        },
      ],
    };

    const marketplace: NavSection = {
      key: 'marketplace',
      label: 'Marketplace',
      visible: hasModule('marketplace_buyer') || hasModule('marketplace_seller'),
      collapsible: true,
      status: hasModule('marketplace_seller') && !activeShop ? 'setup_required' : 'active',
      statusNote: hasModule('marketplace_seller') && !activeShop ? 'Seller setup still needed' : 'Buyer and seller workflows',
      items: [
        {
          key: 'marketplace-home',
          label: 'Marketplace',
          icon: <StoreIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/marketplace',
        },
        {
          key: 'buyer-requests',
          label: 'Request Sourcing',
          icon: <AddShoppingCartIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/buyer-requests',
        },
        ...(hasModule('marketplace_seller')
          ? ([
              {
                key: 'my-shops',
                label: 'My Shops',
                icon: <StoreIcon sx={{ width: 20, height: 20 }} />,
                to: '/dashboard/my-shops',
                match: 'prefix',
              },
              ...(activeShop
                ? ([
                    {
                      key: 'wealth',
                      label: 'Wealth Center',
                      icon: <InsightsIcon sx={{ width: 20, height: 20 }} />,
                      to: `/dashboard/my-shops/${activeShop.id}/wealth`,
                      match: 'prefix',
                    },
                    {
                      key: 'deals',
                      label: 'Deal Room (B2B)',
                      icon: <DealIcon sx={{ width: 20, height: 20 }} />,
                      to: `/dashboard/my-shops/${activeShop.id}/deals`,
                      match: 'prefix',
                    },
                  ] as NavItem[])
                : []),
              {
                key: 'products',
                label: 'Products',
                icon: <InventoryIcon sx={{ width: 20, height: 20 }} />,
                to: '/dashboard/products',
              },
              {
                key: 'orders',
                label: 'Orders',
                icon: <ReceiptIcon sx={{ width: 20, height: 20 }} />,
                to: '/dashboard/orders',
              },
              {
                key: 'import-export',
                label: 'Global Trade Intel',
                icon: <GlobalIcon sx={{ width: 20, height: 20 }} />,
                to: '/dashboard/import-export',
              },
            ] as NavItem[])
          : []),
      ],
    };

    const logistics: NavSection = {
      key: 'logistics',
      label: 'Logistics',
      visible: hasModule('logistics'),
      collapsible: false,
      status: 'active',
      statusNote: 'Shipment coordination workspace',
      items: [
        {
          key: 'logistics-home',
          label: 'Logistics Workspace',
          icon: <LocalShipping sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/logistics',
        },
      ],
    };

    const carbon: NavSection = {
      key: 'carbon',
      label: 'Carbon Credits',
      visible: hasModule('carbon_credits'),
      collapsible: false,
      status: 'active',
      statusNote: 'Participation and readiness',
      items: [
        {
          key: 'carbon-home',
          label: 'Carbon Credits',
          icon: <ForestIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/carbon-credits',
        },
      ],
    };

    const ai: NavSection = {
      key: 'ai',
      label: 'AI & Intelligence',
      visible: true,
      collapsible: true,
      status: 'active',
      statusNote: 'Decision support tools',
      items: [
        {
          key: 'market-forecasts',
          label: 'Market Forecasts',
          icon: <ShowChartIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/ai/market-predictions',
        },
        {
          key: 'recommendations',
          label: 'Recommendations',
          icon: <AutoAwesomeIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/ai/recommendations',
        },
        {
          key: 'iot',
          label: 'IoT Smart Farming',
          icon: <SensorsIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/iot',
        },
        {
          key: 'traceability',
          label: 'Traceability',
          icon: <VerifiedIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/traceability',
        },
        {
          key: 'insurance',
          label: 'Weather Insurance',
          icon: <ShieldIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/insurance',
        },
        {
          key: 'lending',
          label: 'P2P Lending',
          icon: <HandshakeIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/lending',
        },
        {
          key: 'cold-chain',
          label: 'Cold Chain',
          icon: <AcUnitIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/cold-chain',
        },
        {
          key: 'drone-mapping',
          label: 'Drone Mapping',
          icon: <FlightIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/drone-mapping',
        },
      ],
    };

    const community: NavSection = {
      key: 'community',
      label: 'Community',
      visible: true,
      collapsible: false,
      status: 'ready',
      statusNote: 'Trusted discussions and experts',
      items: [
        {
          key: 'community-home',
          label: 'Community Hub',
          icon: <ForumIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/community',
        },
      ],
    };

    const careers: NavSection = {
      key: 'careers',
      label: 'Careers & Labor',
      visible: true,
      collapsible: true,
      status: 'active',
      statusNote: 'Find farm work or hire workers',
      items: [
        {
          key: 'careers-home',
          label: 'Careers Home',
          icon: <WorkOutlineIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/careers',
          match: 'prefix',
        },
        {
          key: 'find-work',
          label: 'Find Work',
          icon: <GroupIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/careers/find-work',
        },
        {
          key: 'post-job',
          label: 'Post a Job',
          icon: <AddShoppingCartIcon sx={{ width: 20, height: 20 }} />,
          to: '/dashboard/careers/post',
        },
      ],
    };

    return [core, farm, marketplace, logistics, careers, carbon, ai, community].filter((section) => section.visible);
  }, [activeShop, hasModule, isDarkMode, muiTheme.palette.primary.main]);

  // Seed sectionOverrides ONCE after the first sections list is computed so that collapsible,
  // status === 'active' sections (Careers & Labor, AI & Intelligence) are expanded by
  // default. Running this as a separate useEffect (with empty deps) avoids calling
  // setState inside a useMemo body, which would otherwise be a React StrictMode
  // anti-pattern with double-invoke risks. activeSectionsSeededRef guards against
  // re-seeding on remounts or subsequent sections changes after the user has toggled.
  useEffect(() => {
    if (activeSectionsSeededRef.current) return;
    const seed: Record<string, boolean> = {};
    for (const s of sections) {
      if (s.collapsible && s.status === 'active') seed[s.key] = true;
    }
    if (Object.keys(seed).length) {
      activeSectionsSeededRef.current = true;
      setSectionOverrides((prev) => (Object.keys(prev).length ? prev : seed));
    }
  }, []);

  const sectionHasActiveItem = (section: NavSection) => section.items.some((item) => isItemActive(item));
  const isSectionOpen = (section: NavSection) =>
    section.collapsible ? (sectionOverrides[section.key] ?? sectionHasActiveItem(section)) : true;

  const toggleSection = (sectionKey: string) => {
    setSectionOverrides((prev) => ({ ...prev, [sectionKey]: !(prev[sectionKey] ?? false) }));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          component={Link}
          to="/"
          onClick={() => {
            if (isMobile) setMobileOpen(false);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            textDecoration: 'none',
            color: 'text.primary',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '10px',
              bgcolor: alpha(muiTheme.palette.primary.main, isDarkMode ? 0.35 : 0.18),
              border: '1px solid',
              borderColor: alpha(muiTheme.palette.primary.main, isDarkMode ? 0.35 : 0.18),
            }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.05 }}>
              FarmersHub
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Dashboard
            </Typography>
          </Box>
        </Box>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} aria-label="Close navigation menu" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider />

      <Box ref={navScrollRef} sx={{ px: 1.25, pt: 1.25, flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ p: 0 }}>
          {sections.map((section) => {
            const active = sectionHasActiveItem(section);
            const open = isSectionOpen(section);
            const statusChip = getSectionStatusProps(section.status);

            return (
              <Box key={section.key} sx={{ mb: 0.75 }}>
                {section.collapsible ? (
                  <ListItemButton
                    onClick={() => toggleSection(section.key)}
                    sx={sectionHeaderSx(active)}
                    aria-label={`${section.label} navigation section`}
                  >
                    <Box sx={{ px: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 650, color: active ? 'text.primary' : 'text.secondary' }}>
                          {section.label}
                        </Typography>
                        <Chip size="small" {...statusChip} sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10, fontWeight: 700 } }} />
                      </Stack>
                      {section.statusNote && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {section.statusNote}
                        </Typography>
                      )}
                    </Box>
                    {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </ListItemButton>
                ) : (
                  <Box
                    sx={{
                      px: 1.75,
                      py: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: section.statusNote ? 0.25 : 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 650,
                          color: active ? 'text.primary' : 'text.secondary',
                        }}
                      >
                        {section.label}
                      </Typography>
                      <Chip size="small" {...statusChip} sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10, fontWeight: 700 } }} />
                    </Stack>
                    {section.statusNote && (
                      <Typography variant="caption" color="text.secondary">
                        {section.statusNote}
                      </Typography>
                    )}
                  </Box>
                )}

                <Collapse in={open} timeout="auto" unmountOnExit={section.collapsible}>
                  {section.items.map((item) => {
                    const selected = isItemActive(item);
                    return (
                      <ListItemButton
                        key={item.key}
                        onClick={() => handleNavigate(item.to)}
                        selected={selected}
                        sx={navItemSx(selected)}
                        aria-label={`Go to ${item.label}`}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: selected ? 'primary.main' : 'text.primary' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500 } }} />
                      </ListItemButton>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>

      <Divider />

      <Box sx={{ px: 1.25, py: 1.25 }}>
        <List sx={{ p: 0 }}>
          <ListItemButton
            onClick={() => handleNavigate('/account')}
            selected={location.pathname === '/account'}
            sx={navItemSx(location.pathname === '/account')}
          >
            <ListItemIcon sx={{ minWidth: 40, color: location.pathname === '/account' ? 'primary.main' : 'text.primary' }}>
              <PersonIcon sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="My Account" primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500 } }} />
          </ListItemButton>

          <ListItemButton
            onClick={() => handleNavigate('/dashboard/subscriptions')}
            selected={location.pathname === '/dashboard/subscriptions'}
            sx={navItemSx(location.pathname === '/dashboard/subscriptions')}
          >
            <ListItemIcon
              sx={{ minWidth: 40, color: location.pathname === '/dashboard/subscriptions' ? 'primary.main' : 'text.primary' }}
            >
              <SubscriptionIcon sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Module Access" primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500 } }} />
          </ListItemButton>

          {hasModule('admin') && (
            <ListItemButton
              onClick={() => handleNavigate('/dashboard/admin')}
              selected={location.pathname === '/dashboard/admin'}
              sx={navItemSx(location.pathname === '/dashboard/admin')}
            >
              <ListItemIcon sx={{ minWidth: 40, color: location.pathname === '/dashboard/admin' ? 'primary.main' : 'text.primary' }}>
                <AdminIcon sx={{ width: 20, height: 20 }} />
              </ListItemIcon>
              <ListItemText primary="Admin Portal" primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500 } }} />
            </ListItemButton>
          )}

          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: '12px',
              mt: 0.5,
              py: 1,
              borderLeft: '3px solid transparent',
              '&:hover': { bgcolor: alpha(muiTheme.palette.error.main, isDarkMode ? 0.18 : 0.1) },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
              <LogoutIcon sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 600, color: 'error.main' } }} />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderBottom: '1px solid',
          borderBottomColor: 'primary.dark',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Home button */}
          <Tooltip title="Home">
            <IconButton aria-label="Go to home page" component={Link} to="/" sx={{ mr: 1 }}>
              <DashboardIcon />
            </IconButton>
          </Tooltip>

          {renderBreadcrumbs()}

          {/* Theme toggle */}
          <LanguageToggle />
          <ThemeToggle />

          {/* Notifications Bell */}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleNotificationMenuOpen}
            sx={{ ml: 1 }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notificationMenuAnchorEl}
            open={notificationMenuOpen}
            onClose={handleNotificationMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                width: 320,
                maxHeight: 400,
                borderRadius: '12px',
                boxShadow: 4,
              }
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">Notifications</Typography>
              <Button size="small" onClick={markAllAsRead}>Mark all read</Button>
            </Box>
            <Divider />
            <List sx={{ p: 0 }}>
              {notifications.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No new notifications</Typography>
                </Box>
              ) : (
                notifications.map((notif) => (
                  <MenuItem 
                    key={notif.id} 
                    onClick={() => handleNotificationClick(notif)}
                    sx={{ 
                      py: 1.5, 
                      px: 2, 
                      borderBottom: '1px solid', 
                      borderColor: 'divider',
                      bgcolor: notif.read ? 'transparent' : 'action.hover',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      whiteSpace: 'normal'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                      {!notif.read && <CircleIcon sx={{ fontSize: 10, color: 'primary.main' }} />}
                      <Typography variant="subtitle2" fontWeight={notif.read ? 500 : 700} sx={{ flexGrow: 1 }}>
                        {notif.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {notif.message}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </List>
            {notifications.length > 0 && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Button fullWidth size="small" component={Link} to="/dashboard/notifications" onClick={handleNotificationMenuClose}>
                  View All Notifications
                </Button>
              </Box>
            )}
          </Menu>

          {/* Profile menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={profileMenuAnchorEl}
            open={profileMenuOpen}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 180,
                bgcolor: 'background.paper',
                color: isDarkMode ? 'text.secondary' : 'text.primary',
                boxShadow: 3,
                borderRadius: 1,
              },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem component={Link} to="/account" onClick={handleProfileMenuClose}>
              <ListItemIcon><PersonIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
              My Account
            </MenuItem>
            <MenuItem component={Link} to="/dashboard/profile" onClick={handleProfileMenuClose}>
              <ListItemIcon><SettingsIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
              Settings
            </MenuItem>
            <MenuItem onClick={handleWalkthroughOpen}>
              <ListItemIcon><HelpOutlineIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
              Walkthrough
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleProfileMenuClose(); handleLogout(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
          <Dialog
            open={walkthroughOpen}
            onClose={() => setWalkthroughOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>{walkthrough.title}</DialogTitle>
            <DialogContent dividers>
              <Stepper activeStep={walkthroughStep} orientation="vertical">
                {walkthrough.steps.map((text, idx) => (
                  <Step key={`${idx}-${text}`}>
                    <StepLabel>{text}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setWalkthroughStep((s) => Math.max(0, s - 1))}
                disabled={walkthroughStep === 0}
              >
                Back
              </Button>
              {walkthroughStep < walkthrough.steps.length - 1 ? (
                <Button
                  onClick={() => setWalkthroughStep((s) => Math.min(walkthrough.steps.length - 1, s + 1))}
                  variant="contained"
                >
                  Next
                </Button>
              ) : (
                <Button onClick={() => setWalkthroughOpen(false)} variant="contained">
                  Done
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>

      {/* Drawer for larger screens */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: '1px solid',
            borderRightColor: 'divider',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Drawer for mobile screens */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        key={location.pathname} // Force re-mount on navigation to fix 'blank page' issues and ensure fresh data
        component="main"
        ref={mainContentRef}
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          mt: '64px', // Height of AppBar
        }}
      >
        {children}
      </Box>
      
      {/* AI Assistant Chatbot */}
      <AIAgronomistChatbot />
    </Box>
  );
};

export default DashboardLayout;
